import { useState, useEffect } from 'react'
import { useVideosContext } from '../../../context/videos-context'
import { Input } from '../../../components/input'
import { Button } from '../../../components/button'
import { Dropdown } from '../../../components/dropdown'
import { ToggleSwitcher } from '../../../components/toggle-switcher'
import { Checkbox } from '../../../components/checkbox'
import { VIDEO_MODE_OPTIONS } from '../../../consts/video-modes'
import * as Styled from './styled'
import { generateVideo } from '../../../context/api-helpers'
import { TVideoMode } from '../../../context/interfaces'

interface IVideoItemProps {
  videoId: string
  index: number
  canRemove: boolean
}

export const VideoItem = ({ videoId, index, canRemove }: IVideoItemProps) => {
  const { state, dispatch } = useVideosContext()
  const video = state.videos.find(v => v.id === videoId)
  const [showRegenerate, setShowRegenerate] = useState(false)

  // Automatically transition from "Completed" to "Regenerate" after 3 seconds
  useEffect(() => {
    if (!video) return

    const isFinished =
      video.generationState === 'finished' && video.renderState === 'completed'

    if (isFinished) {
      const timeoutId = setTimeout(() => {
        setShowRegenerate(true)
      }, 3000)

      return () => clearTimeout(timeoutId)
    } else {
      // Reset to show "Completed" when video starts processing again
      setShowRegenerate(false)
    }
  }, [video, videoId])

  if (!video) return null

  const { styles, models, isInitializing } = state

  const handlePromptChange = (prompt: string) => {
    dispatch({
      type: 'UPDATE_VIDEO',
      payload: {
        id: videoId,
        updates: {
          generateVideoBody: {
            ...video.generateVideoBody,
            prompt
          }
        }
      }
    })
  }

  const handleStyleChange = (styleId: string) => {
    dispatch({
      type: 'UPDATE_VIDEO',
      payload: {
        id: videoId,
        updates: {
          generateVideoBody: {
            ...video.generateVideoBody,
            styleId: Number(styleId)
          }
        }
      }
    })
  }

  const handleModeChange = (mode: string) => {
    dispatch({
      type: 'UPDATE_VIDEO',
      payload: { id: videoId, updates: { mode: mode as TVideoMode } }
    })
  }

  const handleModelChange = (model: string) => {
    dispatch({
      type: 'UPDATE_VIDEO',
      payload: {
        id: videoId,
        updates: {
          generateVideoBody: {
            ...video.generateVideoBody,
            model
          }
        }
      }
    })
  }

  const handleVoiceOverDisabledChange = (isDisabled: boolean) => {
    dispatch({
      type: 'UPDATE_VIDEO',
      payload: {
        id: videoId,
        updates: {
          generateVideoBody: {
            ...video.generateVideoBody,
            isVoiceOverDisabled: isDisabled
          }
        }
      }
    })
  }

  const handleGenerate = async () => {
    const videoToGenerate = state.videos.find(v => v.id === videoId)
    if (!videoToGenerate) return

    // Set generating state
    dispatch({
      type: 'UPDATE_VIDEO',
      payload: {
        id: videoId,
        updates: {
          generationState: 'generating',
          generationProgress: 0,
          generationError: undefined
        }
      }
    })

    try {
      const genAiVideoHash = await generateVideo(videoToGenerate, state.models)

      if (genAiVideoHash) {
        dispatch({
          type: 'UPDATE_VIDEO',
          payload: {
            id: videoId,
            updates: { genAiVideoHash }
          }
        })
      } else {
        // If no hash returned, set error state
        dispatch({
          type: 'UPDATE_VIDEO',
          payload: {
            id: videoId,
            updates: {
              generationState: 'error',
              generationError: 'Failed to start generation'
            }
          }
        })
      }
    } catch (error) {
      dispatch({
        type: 'UPDATE_VIDEO',
        payload: {
          id: videoId,
          updates: {
            generationState: 'error',
            generationError:
              error instanceof Error ? error.message : 'Unknown error'
          }
        }
      })
    }
  }

  const handleRemove = () => {
    dispatch({
      type: 'REMOVE_VIDEO',
      payload: { id: videoId }
    })
  }

  const isGenerating =
    video.generationState === 'generating' ||
    video.generationState === 'preparing_media' ||
    video.generationState === 'preparing_render'
  const isRendering =
    video.renderState === 'preparing' ||
    video.renderState === 'rendering' ||
    video.renderState === 'uploading'
  const isProcessing = isGenerating || isRendering
  const isFinished =
    video.generationState === 'finished' && video.renderState === 'completed'
  const isError =
    video.generationState === 'error' || video.renderState === 'error'

  const getButtonText = () => {
    if (isFinished) return showRegenerate ? '🔄 Regenerate' : '✓ Completed'
    if (isError) return '⚠ Error - Retry'
    if (isRendering) return '🎬 Rendering...'
    if (isGenerating) return '🎬 Generating...'
    return '🎬 Generate'
  }

  return (
    <Styled.VideoItemContainer>
      <Styled.VideoHeader>
        <Styled.VideoNumber>Video #{index + 1}</Styled.VideoNumber>
        {canRemove && (
          <Button onClick={handleRemove} variant="danger">
            Remove
          </Button>
        )}
      </Styled.VideoHeader>

      <Styled.PromptInputSection>
        <Styled.Label>Prompt Text</Styled.Label>
        <Input
          value={video.generateVideoBody.prompt}
          onChange={handlePromptChange}
          placeholder="Describe the video..."
          disabled={isProcessing}
        />
      </Styled.PromptInputSection>

      <Styled.OptionsGrid>
        <Styled.OptionField>
          <ToggleSwitcher
            value={video.mode}
            onChange={handleModeChange}
            options={VIDEO_MODE_OPTIONS}
            label="Mode"
            // disabled={isGenerating}
          />
        </Styled.OptionField>

        {video.mode === 'animated' && (
          <Styled.OptionField>
            <Styled.Label>Model</Styled.Label>
            <Dropdown
              value={video.generateVideoBody.model || ''}
              onChange={handleModelChange}
              options={models.map(model => ({
                id: model.id,
                value: model.model,
                label: model.name
              }))}
              disabled={isInitializing || isProcessing}
            />
          </Styled.OptionField>
        )}

        <Styled.OptionField>
          <Styled.Label>Style</Styled.Label>
          <Dropdown
            value={String(video.generateVideoBody.styleId)}
            onChange={handleStyleChange}
            options={styles.map(style => ({
              id: style.id,
              value: String(style.id),
              label: style.label
            }))}
            disabled={isInitializing || isProcessing}
          />
        </Styled.OptionField>
      </Styled.OptionsGrid>

      <Styled.OptionField>
        <Checkbox
          checked={video.generateVideoBody.isVoiceOverDisabled}
          onChange={handleVoiceOverDisabledChange}
          label="Disable Voice Over"
          disabled={isProcessing}
        />
      </Styled.OptionField>

      <Styled.ActionsRow>
        <Button
          onClick={handleGenerate}
          variant="primary"
          disabled={isProcessing}
        >
          {getButtonText()}
        </Button>
      </Styled.ActionsRow>

      {isProcessing && (
        <Styled.GenerationOverlay>
          <Styled.ProgressContent>
            <Styled.ProgressText>
              {video.generationState === 'generating' &&
                'Starting generation...'}
              {video.generationState === 'preparing_media' &&
                `Preparing media: ${video.generationProgress || 0}%`}
              {video.generationState === 'preparing_render' &&
                'Preparing to render...'}
              {video.renderState === 'preparing' && 'Preparing to render...'}
              {video.renderState === 'rendering' &&
                `Rendering video: ${video.renderProgress || 0}%`}
              {video.renderState === 'uploading' &&
                `Uploading: ${video.renderProgress || 0}%`}
            </Styled.ProgressText>
            {((video.generationProgress !== undefined && isGenerating) ||
              (video.renderProgress !== undefined && isRendering)) && (
              <Styled.ProgressBar
                progress={
                  isRendering
                    ? video.renderProgress || 0
                    : video.generationProgress || 0
                }
              />
            )}
          </Styled.ProgressContent>
        </Styled.GenerationOverlay>
      )}
    </Styled.VideoItemContainer>
  )
}
